import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon,
  DocumentTextIcon,
  UserIcon,
  CogIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const PrivacyPolicy = () => {
  const lastUpdated = "January 18, 2025";

  const sections = [
    {
      id: "information-collection",
      title: "Information We Collect",
      icon: UserIcon,
      content: [
        {
          subtitle: "Personal Information",
          text: "We collect personal information that you provide directly to us, including your name, email address, postal address, date of birth, social security number, employment information, and financial information necessary for account verification and regulatory compliance."
        },
        {
          subtitle: "Trading Information", 
          text: "We collect information about your trading activities, account balances, transaction history, investment preferences, and risk tolerance to provide our services and comply with regulatory requirements."
        },
        {
          subtitle: "Technical Information",
          text: "We automatically collect certain technical information when you use our platform, including IP addresses, browser type, device information, operating system, and usage patterns to improve our services and security."
        }
      ]
    },
    {
      id: "information-use",
      title: "How We Use Your Information",
      icon: CogIcon,
      content: [
        {
          subtitle: "Service Provision",
          text: "We use your information to provide trading services, execute transactions, maintain your account, provide customer support, and send important account notifications and updates."
        },
        {
          subtitle: "Regulatory Compliance",
          text: "We use your information to comply with legal and regulatory requirements, including anti-money laundering (AML) laws, Know Your Customer (KYC) requirements, and tax reporting obligations."
        },
        {
          subtitle: "Security and Fraud Prevention",
          text: "We use your information to protect against fraud, unauthorized access, and other security threats, and to verify your identity for account security purposes."
        }
      ]
    },
    {
      id: "information-sharing",
      title: "Information Sharing and Disclosure",
      icon: ExclamationTriangleIcon,
      content: [
        {
          subtitle: "Regulatory Authorities",
          text: "We may share your information with regulatory authorities, government agencies, and law enforcement when required by law or to comply with legal processes and regulatory requirements."
        },
        {
          subtitle: "Service Providers",
          text: "We may share your information with trusted third-party service providers who assist us in operating our platform, processing payments, providing customer support, and conducting business operations under strict confidentiality agreements."
        },
        {
          subtitle: "Business Transfers",
          text: "In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction, subject to the same privacy protections outlined in this policy."
        }
      ]
    },
    {
      id: "data-security",
      title: "Data Security",
      icon: ShieldCheckIcon,
      content: [
        {
          subtitle: "Security Measures",
          text: "We implement industry-standard security measures including encryption, secure servers, firewalls, and multi-factor authentication to protect your personal and financial information from unauthorized access, disclosure, or misuse."
        },
        {
          subtitle: "Data Retention",
          text: "We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Account information is typically retained for a minimum of seven years after account closure as required by financial regulations."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Your privacy and data protection are our top priorities
          </p>
          <p className="text-sm text-gray-500">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-blue-900 mb-3">Introduction</h2>
          <p className="text-blue-800 leading-relaxed">
            IMEXS BROKERAGE ("we," "our," or "us") is committed to protecting your privacy and maintaining the confidentiality of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our trading platform and services. By using our services, you consent to the collection and use of your information as described in this policy.
          </p>
        </motion.div>

        {/* Main Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                  <section.icon className="h-6 w-6 text-gray-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              
              <div className="space-y-4">
                {section.content.map((item, idx) => (
                  <div key={idx}>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.subtitle}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Your Rights Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-green-50 border border-green-200 rounded-lg p-6 mt-8"
        >
          <h2 className="text-2xl font-bold text-green-900 mb-4">Your Privacy Rights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Access and Correction</h3>
              <p className="text-green-700 text-sm">You have the right to access and correct your personal information held by us.</p>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Data Portability</h3>
              <p className="text-green-700 text-sm">You may request a copy of your personal information in a structured format.</p>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Deletion</h3>
              <p className="text-green-700 text-sm">You may request deletion of your personal information, subject to legal and regulatory requirements.</p>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Opt-out</h3>
              <p className="text-green-700 text-sm">You can opt-out of non-essential communications and marketing materials.</p>
            </div>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <EnvelopeIcon className="h-6 w-6 text-gray-600 mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Contact Us About Privacy</h2>
          </div>
          <p className="text-gray-600 mb-4">
            If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
          </p>
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>Email:</strong> privacy@imexsbrokerage.com</p>
            <p><strong>Address:</strong> 128 City Road, London, United Kingdom, EC1V 2NX</p>
            <p><strong>Response Time:</strong> We will respond to privacy inquiries within 30 days</p>
          </div>
        </motion.div>

        {/* Updates Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="text-center mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          <p className="text-sm text-yellow-800">
            <strong>Policy Updates:</strong> We may update this Privacy Policy from time to time. 
            We will notify you of any material changes by posting the new policy on our website 
            and updating the "Last updated" date above.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 